import { Center, CenterProps, Spinner, Text, VisuallyHidden } from '@chakra-ui/react'

/**
 * Aparência única do indicador de carregamento do painel.
 *
 * O `size` mora aqui e não num `defaultProps` de `Spinner` no `theme.ts` — mesma razão do
 * `FILTER_CONTROL_SIZE`. Um `<Spinner>` do painel aparece em dois papéis diferentes: como
 * indicador autônomo (este componente) e como substituto de ícone dentro de um controle
 * (`icon={loading ? <Spinner size="sm" /> : <Icon name="lni lni-search-alt" />}` no
 * `AddressAutocomplete`, o `xs`
 * ao lado do chevron no `ScheduleClassStatusCell`). O segundo tem que herdar tamanho e cor de
 * quem o contém; um `defaultProps` não sabe distinguir os dois e pintaria de magenta o spinner
 * de dentro do botão.
 *
 * `lg` e não `md`: `md` era o que a maioria tinha, mas por omissão — 15 dos 22 blocos não
 * passavam `size` nenhum. Os que decidiram escolheram maior (`lg`, e `xl` em três), e é essa a
 * escolha deliberada. `3px` de `thickness` é o que os dois `xl` do módulo financeiro usavam.
 */
export const LOADING_SPINNER_SIZE = 'lg'
export const LOADING_SPINNER_THICKNESS = '3px'
export const LOADING_SPINNER_COLOR = 'magenta.500'

/**
 * Espaço vertical que o bloco reivindica, por formato de conteúdo — não medida do Chakra.
 *
 * `section` é o corpo de um modal, um card, o slot de um gráfico: o bloco fica no fluxo e o
 * `py` afasta do que vem antes e depois. Havia `py` 6, 8 e 10 para isso, em três envelopes
 * diferentes (`Flex justify="center"`, `Box textAlign="center"`, `Center`) — e o
 * `ContestsLabelsModal` sem envelope nenhum, com o spinner encostado à esquerda.
 *
 * `page` substitui o corpo inteiro da página. Precisa de altura própria: sem ela a página fica
 * uma faixa de 60px sob o `PageTitle`, que lê como tela quebrada e não como tela carregando.
 */
const LOADING_SIZES: Record<'section' | 'page', CenterProps> = {
  section: { py: 10 },
  page: { minH: '60vh' }
}

export interface ILoadingBlockProps extends Omit<CenterProps, 'children'> {
  /** Formato do espaço que o bloco ocupa. `section` (padrão) para card/modal; `page` para a página inteira. */
  size?: 'section' | 'page'
  /**
   * Substantivo do que está sendo carregado, minúsculo — a mesma convenção de `entity` do
   * `TableEmptyRow`. Rende `Carregando cursos...`; sem ele, o bloco é só o spinner.
   */
  entity?: string
  /** Substitui a frase montada, para o que não é "carregando \<coisa\>": `Lendo a planilha e conferindo os cadastros`. */
  label?: string
}

/**
 * Bloco de carregamento do painel: spinner centralizado, com texto opcional.
 *
 * Serve os dois lugares em que o painel espera por dados sem ter linha de tabela para mostrar —
 * a página inteira e uma seção dentro dela (corpo de modal, card, gráfico). Eram ~22 blocos de
 * spinner com quatro geometrias e três envelopes, mais 9 páginas cujo carregamento inteiro era
 * um `<Text>Carregando...</Text>` sem indicação de movimento.
 *
 * A frase é montada a partir de `entity` — a página declara o que está buscando, não a cópia.
 * O painel tinha as duas reticências em uso ao mesmo tempo (`...` em 28 sites, `…` em 3); agora
 * há uma só, e ela está aqui.
 *
 * Para um slot que já tem altura própria — o gráfico de 350px do `PerformanceChart`, os 300px do
 * `PickupInfoModal` — passe a medida (`h`, `minH`) e o spinner centraliza nela. Decoração é do
 * componente, medida é de quem o contém: a mesma divisão do `contentProps` do `FormModal`.
 */
export default function LoadingBlock({ size = 'section', entity, label, ...rest }: ILoadingBlockProps) {
  const text = label ?? (entity ? `Carregando ${entity}` : undefined)

  return (
    // `role="status"` (que já implica `aria-live="polite"`) põe o anúncio no bloco, e não no
    // spinner: quem fala é o texto abaixo, visível ou não. O spinner é decoração, então sai da
    // árvore de acessibilidade com `aria-hidden` — inclusive o `<span srOnly>` que o Chakra
    // renderiza dentro dele. O `label=""` só mantém o DOM limpo; a garantia é o `aria-hidden`,
    // que não depende de como esta ou a próxima versão do Chakra trata um `label` vazio.
    <Center role="status" flexDirection="column" gap={3} w="100%" {...LOADING_SIZES[size]} {...rest}>
      {/* `emptyColor` é a única escolha nova aqui: nenhum dos 22 blocos passava trilha, e um arco
          magenta solto some sobre fundo branco no `3px`. O resto é o que os que decidiram já tinham. */}
      <Spinner
        aria-hidden
        label=""
        size={LOADING_SPINNER_SIZE}
        thickness={LOADING_SPINNER_THICKNESS}
        color={LOADING_SPINNER_COLOR}
        emptyColor="gray.100"
      />
      {text ? <Text variant="caption">{text}...</Text> : <VisuallyHidden>Carregando</VisuallyHidden>}
    </Center>
  )
}
